"use client";

import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { FormHoneypot } from "@/shared/components/FormHoneypot";
import { coreServices } from "@/shared/config/coreServices";
import {
  customerSupportEmail,
  customerSupportEmailHref,
  isSiteActive,
} from "@/shared/config/customerSupport";
import { usePublicFormGuard } from "@/shared/hooks/usePublicFormGuard";
import styles from "./InquiryModal.module.scss";

type FieldName = "name" | "phone" | "location" | "service" | "message";

function validateInquiry(form: HTMLFormElement): Partial<Record<FieldName, string>> {
  const data = new FormData(form);
  const value = (name: FieldName) => String(data.get(name) ?? "").trim();
  const errors: Partial<Record<FieldName, string>> = {};
  if (value("name").length < 2 || value("name").length > 100 || !/\p{L}/u.test(value("name"))) {
    errors.name = "Enter your name using 2 to 100 characters.";
  }
  const phone = value("phone");
  const digits = phone.replace(/\D/g, "");
  if (!/^\+?[\d\s().-]+$/.test(phone) || digits.length < 10 || digits.length > 15 || /^(\d)\1+$/.test(digits) || phone.length > 30) {
    errors.phone = "Enter a valid phone number with 10 to 15 digits, including the country code if needed.";
  }
  if (value("location").length > 160) errors.location = "Keep the care location within 160 characters.";
  if (value("service") && !coreServices.some((service) => service.title === value("service"))) {
    errors.service = "Choose a care service from the list.";
  }
  if (value("message").length < 10 || value("message").length > 2_000) {
    errors.message = "Describe your care requirement using 10 to 2,000 characters.";
  }
  return errors;
}

export function InquiryModal({ onClose, open }: { onClose: () => void; open: boolean }) {
  // Mount a fresh form on each opening while the shared browser attempt history persists.
  return open ? <InquiryDialog onClose={onClose} /> : null;
}

function InquiryDialog({ onClose }: { onClose: () => void }) {
  const [sent, setSent] = useState(false);
  const [showUnavailableNotice, setShowUnavailableNotice] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});
  const dialogRef = useRef<HTMLElement>(null);
  const confirmationRef = useRef<HTMLDivElement>(null);
  const unavailableCloseRef = useRef<HTMLButtonElement>(null);
  const unavailableNoticeRef = useRef(false);
  const onCloseRef = useRef(onClose);
  const guard = usePublicFormGuard("inquiry");
  const id = useId();

  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  useEffect(() => { unavailableNoticeRef.current = showUnavailableNotice; }, [showUnavailableNotice]);

  useEffect(() => {
    if (sent) confirmationRef.current?.focus();
  }, [sent]);

  useEffect(() => {
    if (showUnavailableNotice) unavailableCloseRef.current?.focus();
  }, [showUnavailableNotice]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.body.style.overflow = "hidden";
    const focusFrame = window.requestAnimationFrame(() => dialogRef.current?.querySelector<HTMLInputElement>('input[name="name"]')?.focus());

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        if (unavailableNoticeRef.current) {
          setShowUnavailableNotice(false);
          return;
        }
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]',
      )).filter((element) => element.tabIndex >= 0 && !element.closest('[aria-hidden="true"]') && element.getClientRects().length > 0);
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!dialogRef.current.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first)?.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
      previouslyFocused?.focus();
    };
  }, []);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!isSiteActive) {
      setShowUnavailableNotice(true);
      return;
    }
    if (guard.honeypotRef.current?.value) return;
    const nextErrors = validateInquiry(event.currentTarget);
    setErrors(nextErrors);
    const firstInvalid = Object.keys(nextErrors)[0];
    if (firstInvalid) {
      event.currentTarget.querySelector<HTMLElement>(`[name="${firstInvalid}"]`)?.focus();
      return;
    }
    if (!guard.beginAttempt()) return;
    try {
      // Preserve the original local confirmation; no inquiry API delivery occurs here.
      setSent(true);
    } finally {
      guard.endAttempt();
    }
  }

  const fieldProps = (name: FieldName) => ({
    "aria-invalid": Boolean(errors[name]),
    "aria-describedby": errors[name] ? `${id}-${name}-error` : undefined,
  });
  const fieldError = (name: FieldName) => errors[name]
    ? <span className={styles.error} id={`${id}-${name}-error`}>{errors[name]}</span>
    : null;

  return (
    <div className="wireframe-inquiry-backdrop" role="presentation" onMouseDown={onClose}>
      <section aria-labelledby={`${id}-title`} aria-describedby={`${id}-description`} aria-modal="true" className="wireframe-inquiry-modal" ref={dialogRef} role="dialog" onMouseDown={(event) => event.stopPropagation()}>
        <button aria-label="Close inquiry form" className="wireframe-inquiry-close" type="button" onClick={onClose}>×</button>
        <h2 id={`${id}-title`}>Book a Care Visit</h2>
        <p id={`${id}-description`}>Share the basic care requirement and preferred contact details. The Carevinco team can coordinate the next step.</p>
        <form className="wireframe-inquiry-form" noValidate onChange={() => setSent(false)} onSubmit={handleSubmit}>
          <FormHoneypot inputRef={guard.honeypotRef} />
          <input {...fieldProps("name")} aria-label="Your name" autoComplete="name" maxLength={100} name="name" placeholder="Your name" required />
          {fieldError("name")}
          <input {...fieldProps("phone")} aria-label="Phone number" autoComplete="tel" maxLength={30} name="phone" placeholder="Phone number" required type="tel" />
          {fieldError("phone")}
          <input {...fieldProps("location")} aria-label="Care location" autoComplete="address-level2" maxLength={160} name="location" placeholder="Care location" />
          {fieldError("location")}
          <select {...fieldProps("service")} aria-label="Care service" defaultValue="" name="service">
            <option disabled value="">Select care service</option>
            {coreServices.map((service) => <option key={service.slug} value={service.title}>{service.title}</option>)}
          </select>
          {fieldError("service")}
          <textarea {...fieldProps("message")} aria-label="Care requirement, date, time and patient notes" maxLength={2_000} name="message" placeholder="Care requirement, date, time and patient notes" required />
          {fieldError("message")}
          {guard.error ? <div className={styles.error} role="alert">{guard.error}</div> : null}
          {sent ? (
            <div className="wireframe-success" ref={confirmationRef} tabIndex={-1}>
              Thank you. Your inquiry has been captured.
            </div>
          ) : null}
          <div className={styles.status} aria-live="off" role="timer">
            {guard.retryAfterSeconds > 0 ? `Please wait ${guard.retryAfterSeconds} seconds before trying again.` : null}
          </div>
          <button className={`btn btn--primary ${styles.submit}`} disabled={guard.isSubmitting || guard.retryAfterSeconds > 0} type="submit">Submit Inquiry</button>
        </form>
        {showUnavailableNotice ? (
          <div className={styles.unavailableBackdrop} role="presentation" onMouseDown={() => setShowUnavailableNotice(false)}>
            <section
              aria-labelledby={`${id}-unavailable-title`}
              aria-modal="true"
              className={styles.unavailableDialog}
              role="alertdialog"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <span aria-hidden="true" className={styles.unavailableIcon}>i</span>
              <h3 id={`${id}-unavailable-title`}>Support is available</h3>
              <p>
                This form is in Progress, Support Available at{" "}
                <a href={customerSupportEmailHref}>{customerSupportEmail}</a>
              </p>
              <div className={styles.unavailableActions}>
                <a href={customerSupportEmailHref}>Email support</a>
                <button ref={unavailableCloseRef} type="button" onClick={() => setShowUnavailableNotice(false)}>Close</button>
              </div>
            </section>
          </div>
        ) : null}
      </section>
    </div>
  );
}
